import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EdgeLinkSiteWithDevicesDto, BridgeRoute } from "@/lib/api/edgelink";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@radix-ui/react-collapsible";
import { Building2, ChevronUp, ChevronDown, Server } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

interface BridgeSiteGroupProps {
  site: EdgeLinkSiteWithDevicesDto;
  routes: BridgeRoute[];
  defaultExpanded?: boolean;
}

export function BridgeSiteGroup({ site, routes, defaultExpanded = false }: BridgeSiteGroupProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Check if this bridge is the default route for this site
  const isDefaultRoute = routes.some((r) => r.id === site.defaultRouteNamespaceId);
  const onlineCount = site.devices.filter((d) => d.isOnline).length;
  const offlineCount = site.devices.filter((d) => d.isOnline === false).length;

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div className="border rounded-lg border-zinc-800 bg-card2/50 overflow-hidden">
        <CollapsibleTrigger asChild>
          <div
            className="flex items-center justify-between p-3 cursor-pointer hover:bg-zinc-800/30 transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className={cn(
                "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border",
                isDefaultRoute ? "bg-lime/10 border-lime/30" : "bg-background border-zinc-700"
              )}>
                <Building2 className={cn("h-4 w-4", isDefaultRoute ? "text-lime" : "text-muted-foreground")} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link
                    to={`/customer/${site.companyId}/project/${site.id}`}
                    className="font-medium text-sm text-white hover:text-lime hover:underline truncate"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {site.name}
                  </Link>
                  {isDefaultRoute && (
                    <Badge variant="outline" className="text-[10px] py-0 border-lime/40 text-lime bg-lime/5">
                      Default Route
                    </Badge>
                  )}
                  {site.isActive === false && (
                    <Badge variant="secondary" className="text-[10px] py-0">Inactive</Badge>
                  )}
                  {site.archivedAt && (
                    <Badge variant="outline" className="text-[10px] py-0 border-yellow-500/30 text-yellow-500">Archived</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">{site.companyName}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {site.deviceCount > 0 ? (
                <div className="flex items-center gap-1.5 text-xs">
                  {onlineCount > 0 && (
                    <span className="flex items-center gap-1 text-emerald-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      {onlineCount}
                    </span>
                  )}
                  {offlineCount > 0 && (
                    <span className="flex items-center gap-1 text-zinc-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-zinc-500" />
                      {offlineCount}
                    </span>
                  )}
                  {onlineCount === 0 && offlineCount === 0 && (
                    <span className="text-muted-foreground">{site.deviceCount} device{site.deviceCount !== 1 ? 's' : ''}</span>
                  )}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">No devices</span>
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7">
                {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t border-zinc-800 bg-zinc-900/30">
            {site.devices.length > 0 ? (
              <div className="divide-y divide-zinc-800/50">
                {site.devices.map((device) => (
                  <Link
                    key={device.id}
                    to={`/devices/${device.id}/edit`}
                    className="flex items-center justify-between px-4 py-2.5 hover:bg-zinc-800/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-7 w-7 items-center justify-center rounded bg-zinc-800/50">
                        <Server className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-medium hover:text-lime">{device.name}</p>
                        {device.type && (
                          <p className="text-xs text-muted-foreground">{device.type}</p>
                        )}
                      </div>
                    </div>
                    {device.isOnline !== undefined && (
                      <div className={cn(
                        "flex items-center gap-1.5 text-xs",
                        device.isOnline ? "text-emerald-400" : "text-zinc-500"
                      )}>
                        <div className={cn(
                          "w-1.5 h-1.5 rounded-full",
                          device.isOnline ? "bg-emerald-400" : "bg-zinc-500"
                        )} />
                        {device.isOnline ? 'Online' : 'Offline'}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No devices at this site
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
